<template>
  <div class="layout-px-spacing">
    <teleport to="#breadcrumb">
      <ul class="navbar-nav flex-row">
        <li>
          <div class="page-header">
            <nav class="breadcrumb-one" aria-label="breadcrumb">
              <ol class="breadcrumb">
                <li class="breadcrumb-item active" aria-current="page">
                  <span>Add Violation Action</span>
                </li>
              </ol>
            </nav>
          </div>
        </li>
      </ul>
    </teleport>

    <div class="row layout-top-spacing">
      <div class="col-xl-12">
        <div class="panel br-4">
          <div class="panel-body">
            <h6>Create Violation Action!</h6>
            <form @submit.prevent="submitReport">
              <!-- Violation Select -->
              <div class="form-group">
                <label for="violation">Select Violation</label>
                <multiselect v-model="violationAction.violation_id" :options="filteredViolations" :searchable="true"
                  :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                  placeholder="Select a Violation" label="title" track-by="id">
                </multiselect>
              </div>

              <!-- Main Form Fields -->
              <div class="row mt-3">
                <!-- Left -->
                <div class="col-md-6">
                  <div class="form-group mt-3">
                    <label for="employee">Select Violator</label>
                    <multiselect v-model="violationAction.employee_id" :options="filteredDrivers" :searchable="true"
                      :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                      placeholder="Select a Violator" label="driver" track-by="employee_id">
                    </multiselect>
                  </div>

                  <div class="form-group mt-3">
                    <label for="truck">Truck</label>
                    <multiselect v-model="violationAction.truck_id" :options="filteredTrucks" :searchable="true"
                      :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                      placeholder="Select a Truck" label="registration_number" track-by="id" />
                  </div>

                  <div class="form-group mt-3">
                    <label for="trailer">Trailer</label>
                    <multiselect v-model="violationAction.trailer_id" :options="filteredTrailers" :searchable="true"
                      :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                      placeholder="Select a Trailer" label="registration_number" track-by="id" />
                  </div>
                </div>

                <!-- Right -->
                <div class="col-md-6">
                  <div class="form-group mt-3">
                    <label for="loading_order_item">Loading Order</label>
                    <multiselect v-model="violationAction.loading_order_item_id" :options="filteredLoadingOrders"
                      :searchable="true" :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                      placeholder="Select a Loading Order" :custom-label="loadingOrderLabel" track-by="id" />
                  </div>

                  <div class="form-group mt-3">
                    <label for="actionTaken">Action Taken</label>
                    <!-- <select v-model="violationAction.description" id="description" class="form-control" required>
                      <option value="" disabled>Select action taken</option>
                      <option value="Verbal Warning">Verbal Warning</option>
                      <option value="Warning Letter">Warning Letter</option>
                      <option value="Penalty">Penalty</option>
                      <option value="Benching">Benching</option>
                      <option value="Attending Training">Attending Training</option>
                      <option value="Termination">Termination</option>
                    </select> -->
                    <multiselect v-model="violationAction.taken_action_id" :options="filteredActionsTaken"
                      :searchable="true" :close-on-select="true" :clear-on-select="false" :preserve-search="true"
                      placeholder="Select an Action Taken" label="name" track-by="id" />
                  </div>

                  <div v-if="parameters.length" class="row">
                    <div v-for="param in parameters" :key="param.id" class="col-md-6 mt-3">
                      <label :for="'param-' + param.id">{{ param.name }}</label>

                      <!-- Text Input -->
                      <input v-if="param.datatype === 'text'" type="text" class="form-control"
                        v-model="violationAction.parameters[param.name]" :id="'param-' + param.id">

                      <!-- Number Input -->
                      <input v-else-if="param.datatype === 'number'" type="number" class="form-control"
                        v-model.number="violationAction.parameters[param.name]" :id="'param-' + param.id">

                      <!-- Date-Time Input -->
                      <input v-else-if="param.datatype === 'datetime-local'" type="datetime-local" class="form-control"
                        v-model="violationAction.parameters[param.name]" :id="'param-' + param.id">

                      <!-- Time Input -->
                      <input v-else-if="param.datatype === 'time'" type="time" class="form-control"
                        v-model="violationAction.parameters[param.name]" :id="'param-' + param.id">

                      <!-- Default to text if unknown type -->
                      <input v-else type="text" class="form-control" v-model="violationAction.parameters[param.name]"
                        :id="'param-' + param.id">
                    </div>
                  </div>
                </div>
              </div>

              <!-- Submit -->
              <div class="text-center mt-3">
                <div v-if="loading">
                  <button type="button" class="btn btn-success btn-lg mb-3 me-3"><span
                      class="spinner-grow text-white me-2 align-self-center loader-sm">Loading...</span>
                    Processing
                  </button>
                </div>
                <div v-else>
                  <button type="submit" class="btn btn-success btn-lg mb-3 me-3">Submit</button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import axios from 'axios';
import { BASE_URL, BASE_URL_2, BASE_URL_6 } from '@/Config/config';
import { useMeta } from '@/composables/use-meta';
import Swal from 'sweetalert2';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import { useStore } from 'vuex';

useMeta({ title: 'Add Violation Action' });

const loading = ref(false);
const store = useStore();

// Reactive variables
const drivers = ref([]);
const violations = ref([]);
const parameters = ref([]);
const trucks = ref([]);
const trailers = ref([]);
const loading_order_item = ref([]);
const actionsTakenList = ref([]);

// Search Values
const violationSearch = ref('');
const employeeSearch = ref('');
const truckSearch = ref('');
const trailerSearch = ref('');
const loadingOrderSearch = ref('');
const actionsTakenSearch = ref('');

const driverLabel = (option) => `${option.driver}`;

const loadingOrderLabel = (item) => {
  return `ID: ${item.id} | Truck: ${item.truck} | Trailer: ${item.trailer} | Type: ${item.type}`;
};

const violationAction = ref({
  violation_id: '',
  truck_id: '',
  trailer_id: '',
  loading_order_item_id: '',
  taken_action_id: '',
  time: '',
  location: '',
  employee_id: '',
  parameters: {}
});

// SweetAlert functions
const showSuccessAlert = (message) => {
  Swal.fire({
    icon: 'success',
    title: 'Success!',
    text: message,
    padding: '2em',
    timer: 3000,
    timerProgressBar: true,
    showConfirmButton: false
  });
};

const showErrorAlert = (message) => {
  Swal.fire({
    icon: 'error',
    title: 'Error!',
    text: message,
    padding: '2em',
    confirmButtonColor: '#3085d6',
    confirmButtonText: 'OK'
  });
};

const filteredViolations = computed(() => {
  if (!violationSearch.value) return violations.value;
  const term = violationSearch.value.toLowerCase();
  return violations.value.filter(violation =>
    violation.title.toLowerCase().includes(term))
});

const filteredDrivers = computed(() => {
  if (!employeeSearch.value) return drivers.value;
  const term = employeeSearch.value.toLowerCase();
  return drivers.value.filter(employee =>
    employee.driver.toLowerCase().includes(term))
});

const filteredTrucks = computed(() => {
  if (!truckSearch.value) return trucks.value;
  const term = truckSearch.value.toLowerCase();
  return trucks.value.filter(truck =>
    truck.registration_number.toLowerCase().includes(term))
});

const filteredTrailers = computed(() => {
  if (!trailerSearch.value) return trailers.value;
  const term = trailerSearch.value.toLowerCase();
  return trailers.value.filter(trailer =>
    trailer.registration_number.toLowerCase().includes(term))
});

const filteredLoadingOrders = computed(() => {
  if (!loadingOrderSearch.value) return loading_order_item.value;

  const term = loadingOrderSearch.value.toLowerCase();
  return loading_order_item.value.filter(item => {
    return (
      String(item.id).toLowerCase().includes(term) ||
      (item.truck && item.truck.toLowerCase().includes(term)) ||
      (item.trailer && item.trailer.toLowerCase().includes(term)) ||
      (item.type && item.type.toLowerCase().includes(term))
    );
  });
});

const filteredActionsTaken = computed(() => {
  if (!actionsTakenSearch.value) return actionsTakenList.value;
  const term = actionsTakenSearch.value.toLowerCase();
  return actionsTakenList.value.filter(action =>
    action.name.toLowerCase().includes(term))
});

const loadViolations = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/violations`, {
      headers: {
        'Authorization': `Bearer ${store.state.token}`,
        'X-Company-ID': store.state.currentCompany?.id
      }
    });
    violations.value = response.data;
    console.log('Violations responded:', response.status);
  } catch (error) {
    console.error('Error loading violations:', error);
    showErrorAlert('Failed to load violations. Please try again later.');
  }
};

const loadEmployees = async () => {
  try {
    const response = await axios.get(`${BASE_URL_2}/employee/driver/show`);
    drivers.value = response.data.drivers;
    console.log("Drivers responded:", response.status);
  } catch (error) {
    console.error('Error fetching employees:', error);
    showErrorAlert('Failed to load drivers. Please try again later.');
  }
};

const loadTrucks = async () => {
  try {
    const response = await axios.get(`${BASE_URL_2}/vehicle/truck/show`);
    trucks.value = response.data.trucks || response.data.data || response.data;
    console.log("Trucks responded:", response.status);
  } catch (error) {
    console.error('Error fetching trucks:', error);
    showErrorAlert('Failed to load trucks. Please try again later.');
  }
};

const loadTrailers = async () => {
  try {
    const response = await axios.get(`${BASE_URL_2}/vehicle/trailer/show`);
    trailers.value = response.data.trailers || response.data.data || response.data;
    console.log("Trailers responded:", response.status);
  } catch (error) {
    console.error('Error fetching trailers:', error);
    showErrorAlert('Failed to load trailers. Please try again later.');
  }
};

const fetchLoadingOrder = async () => {
  try {
    loading.value = true;
    const response = await axios.get(`${BASE_URL_2}/loading-order-item/show`);
    loading_order_item.value = response.data.loading_order_items;
    console.log("Loading orders responded:", response.status);
  } catch (error) {
    console.error('Error fetching loading order items:', error);
    showErrorAlert('Failed to load loading orders. Please try again later.');
  } finally {
    loading.value = false;
  }
};

const loadActionsTaken = async () => {
  try {
    const response = await axios.get(`${BASE_URL}/taken-actions`, {
      headers: {
        'Authorization': `Bearer ${store.state.token}`,
        'X-Company-ID': store.state.currentCompany?.id
      }
    });
    actionsTakenList.value = response.data;
    console.log('Actions taken responded:', response.status);
  } catch (error) {
    console.error('Error loading actions taken:', error);
    showErrorAlert('Failed to load actions taken. Please try again later.');
  }
};;

watch(() => violationAction.value.violation_id, async (newVal) => {
  const violationId = newVal?.id || newVal;
  if (violationId) {
    try {
      const response = await axios.get(`${BASE_URL}/violations/${violationId}`, {
        headers: {
          'Authorization': `Bearer ${store.state.token}`,
          'X-Company-ID': store.state.currentCompany?.id
        }
      });
      const violationData = response.data;

      parameters.value = violationData.parameters || [];
      violationAction.value.parameters = {};

      parameters.value.forEach(param => {
        violationAction.value.parameters[param.name] = '';
      });

      console.log('Parameters loaded:', parameters.value);
    } catch (error) {
      console.error('Error loading violation parameters:', error);
      parameters.value = [];
      violationAction.value.parameters = {};
      showErrorAlert('Failed to load parameters for this violation');
    }
  } else {
    parameters.value = [];
    violationAction.value.parameters = {};
  }
}, { immediate: true });

const submitReport = async () => {
  try {
    loading.value = true;

    // Prepare the payload with string IDs
    const payload = {
      ...violationAction.value,
      employee_id: violationAction.value.employee_id?.employee_id?.toString() || '',
      truck_id: violationAction.value.truck_id?.id?.toString() || '',
      trailer_id: violationAction.value.trailer_id?.id?.toString() || '',
      loading_order_item_id: violationAction.value.loading_order_item_id?.id?.toString() || '',
      taken_action_id: typeof violationAction.value.taken_action_id === 'object'
        ? violationAction.value.taken_action_id.id
        : violationAction.value.taken_action_id,
      violation_id: typeof violationAction.value.violation_id === 'object'
        ? violationAction.value.violation_id.id
        : violationAction.value.violation_id,
      parameters: violationAction.value.parameters,
      truck: violationAction.value.truck_id?.registration_number || '',
      trailer: violationAction.value.trailer_id?.registration_number || '',
      employee: violationAction.value.employee_id?.driver || ''
    };

    console.log('Submitting payload:', payload);

    const response = await axios.post(`${BASE_URL}/violation-actions`,
      payload, {
      headers: {
        'Authorization': `Bearer ${store.state.token}`,
        'X-Company-ID': store.state.currentCompany?.id
      }
    });

    if (response.data.success) {
      showSuccessAlert("Violation Action submitted successfully!");

      // Reset form
      violationAction.value = {
        violation_id: '',
        truck_id: '',
        trailer_id: '',
        loading_order_item_id: '',
        description: '',
        time: '',
        location: '',
        employee_id: '',
        parameters: {}
      };

      // Reset search fields
      employeeSearch.value = '';
      truckSearch.value = '';
      trailerSearch.value = '';
      loadingOrderSearch.value = '';
    } else {
      showErrorAlert(response.data.message || "Error submitting report");
    }
  } catch (error) {
    console.error("Submission error:", error);

    let errorMessage = "Failed to submit violation action";
    if (error.response) {
      if (error.response.data?.errors) {
        errorMessage = Object.entries(error.response.data.errors)
          .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
          .join('\n');
      } else if (error.response.data?.message) {
        errorMessage = error.response.data.message;
      }
    } else if (error.request) {
      errorMessage = "Network error - please check your connection";
    }

    showErrorAlert(errorMessage);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  Promise.all([
    loadViolations(),
    loadEmployees(),
    loadTrucks(),
    loadTrailers(),
    fetchLoadingOrder(),
    loadActionsTaken(),
    fetchStores()
  ]).catch(error => {
    console.error('Error loading initial data:', error);
  });
});
</script>

<style scoped>
.layout-px-spacing {
  min-height: calc(100vh - 170px) !important;
}
</style>
